import { CSSProperties } from 'react';
import { Saturation, Hue, Checkboard, EditableInput } from 'react-color/lib/components/common';
import { CheckboardProps, CustomPicker } from 'react-color';

type StylesProps = {
  input?: CSSProperties | undefined;
  label?: CSSProperties | undefined;
  wrap?: CSSProperties | undefined;
};

type CheckboardPropsWithRenderers = CheckboardProps & {
  renderers: Record<string, string>;
};

const hexStyles: StylesProps = {
  wrap: {
    width: '80px',
    height: '32px',
    borderRadius: '3px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  input: {
    minWidth: '80px',
    width: '80px',
    minHeight: '32px',
    height: '32px',
    borderRadius: '3px',
    border: 'solid 1px #d5d5db',
    fontSize: '12px',
    color: '#585870',
    padding: '0.5rem',
  },
  label: {
    marginTop: '1rem',
    fontSize: '12px',
    color: '#828294',
  },
};

const rgbStyles: StylesProps = {
  wrap: {
    width: '40px',
    height: '32px',
    borderRadius: '3px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginLeft: '1rem',
  },
  input: {
    minWidth: '40px',
    width: '40px',
    minHeight: '32px',
    height: '32px',
    borderRadius: '3px',
    border: 'solid 1px #d5d5db',
    fontSize: '12px',
    color: '#585870',
    padding: '0.5rem',
  },
  label: {
    marginTop: '1rem',
    fontSize: '12px',
    color: '#828294',
  },
};

const CustomPointer = () => (
  <div className="border-neutral-40 -my-1 h-10 w-4 rounded-xs border border-solid bg-white" />
);

type HexColor = `#${string}`;

export type CustomColorPickerProps = {
  rgb: { r: number; g: number; b: number; a: number };
  hex: HexColor;
  onChange: (
    value: {
      hex?: HexColor;
      R?: string | undefined;
      B?: string | undefined;
      G?: string | undefined;
    },
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  hsl: { a: number; h: number; l: number; s: number };
  hsv: { a: number; h: number; l: number; s: number };
  color: string;
};

type dataProps = {
  hsl: {
    a?: number | undefined;
    h: number;
    l: number;
    s: number;
  };
  rgb: {
    a?: number | undefined;
    b: number;
    g: number;
    r: number;
  };
  HEX?: string;
  R?: string | undefined;
  B?: string | undefined;
  G?: string | undefined;
};

type functionsProps = {
  data: dataProps;
  event: React.ChangeEvent<HTMLInputElement>;
};

const CustomColorPicker = ({ rgb, hex, onChange, ...props }: CustomColorPickerProps) => {
  const handleOnChangeHEX = (data: functionsProps['data'], event: functionsProps['event']) => {
    const newBackgroundColor: {
      [key: string]: string | undefined | { a: number; h: number; l: number; s: number };
    } = { ...props, hex };
    const key = Object.keys(data)[0].toLowerCase();
    newBackgroundColor[key] = data.HEX;
    onChange(newBackgroundColor, event);
  };

  const handleOnChangeRGB = (data: functionsProps['data'], event: functionsProps['event']) => {
    const newBackgroundColor: {
      [key: string]: number | undefined | { r: number; g: number; b: number; a: number };
    } = { ...rgb };
    const key = Object.keys(data)[0].toLowerCase();
    let value = parseInt(data.R || data.G || data.B || '', 10);

    if (value > 255) {
      value = 255;
    } else if (value < 0) {
      value = 0;
    }

    newBackgroundColor[key] = value;
    onChange(newBackgroundColor, event);
  };

  const handleHueChange = (data: functionsProps['data'], event: functionsProps['event']) => {
    onChange(data, event);
  };

  const handleOnSaturationChange = (
    data: functionsProps['data'],
    event: functionsProps['event'],
  ) => {
    onChange(data, event);
  };

  const hueProps = { ...props, radius: '2px' };

  // Temporary workaround for a react-color Checkboard bug.
  // react-color 2.19.3 expects `renderers` to be defined,
  // so we provide an empty object for compatibility.
  const checkboardProps: CheckboardPropsWithRenderers = {
    white: hex,
    grey: hex,
    renderers: {},
  };

  return (
    <div className="flex flex-col items-center">
      <div className="border-neutral-10 relative h-89.5 w-152 rounded-xs border border-solid">
        <Saturation {...props} onChange={handleOnSaturationChange} />
      </div>
      <div className="mt-8 mb-12 flex">
        <div className="flex flex-col">
          <div className="relative h-8 w-120">
            <Hue
              pointer={CustomPointer}
              direction="horizontal"
              {...hueProps}
              onChange={handleHueChange}
            />
          </div>
          <div className="mt-4 flex">
            <EditableInput onChange={handleOnChangeHEX} style={hexStyles} label="HEX" value={hex} />
            <div className="ml-8 flex flex-row">
              <EditableInput
                onChange={handleOnChangeRGB}
                style={rgbStyles}
                label="R"
                value={rgb.r}
              />
              <EditableInput
                onChange={handleOnChangeRGB}
                style={rgbStyles}
                label="G"
                value={rgb.g}
              />
              <EditableInput
                onChange={handleOnChangeRGB}
                style={rgbStyles}
                label="B"
                value={rgb.b}
              />
            </div>
          </div>
        </div>

        <div
          className="border-neutral-10 relative ml-8 h-28 w-24 rounded-[3px] border border-solid"
          style={{ backgroundColor: hex }}
        >
          <Checkboard {...checkboardProps} />
        </div>
      </div>
    </div>
  );
};

export default CustomPicker(CustomColorPicker);
